// The deploy dialog. Opening it starts the deploy (the SSE stream at
// /deploy/stream runs it) and shows the log as it arrives. A Modal like
// every dialog: Escape, the scrim and Close shut it at any time, and a
// bottom Sheet on phones. Closing while it runs does not stop the deploy
// (the server finishes it), which the Close button says.
//
// Opened from the deploy banner (after "Deploy changes?") and from the
// org chart's ConfigDrawer.

import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle, Loader2, XCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { cx } from "@/components/ui/controlStyles";
import { useAuth } from "@/stores/auth";

/** lost = the stream dropped before "done": the deploy may still be running. */
type Status = "running" | "done" | "error" | "lost";

interface LogLine {
  text: string;
  error?: boolean;
}

interface DeployModalProps {
  onClose: () => void;
}

const TITLE: Record<Status, string> = {
  running: "Deploying",
  done: "Deployed",
  error: "Deploy didn't finish",
  lost: "Lost contact with the deploy",
};

const SENTENCE: Record<Status, string> = {
  running: "Updating your agents…",
  done: "Done. Your agents are back.",
  error: "It stopped with an error. The log below shows where.",
  lost: "It may still be running. Check again in a minute.",
};

export function DeployModal({ onClose }: DeployModalProps) {
  const customerId = useAuth((s) => s.customerId) ?? "";
  const [lines, setLines] = useState<LogLine[]>([]);
  const [status, setStatus] = useState<Status>("running");
  const scrollRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  useEffect(() => {
    const eventSource = new EventSource(`/api/customers/${customerId}/deploy/stream`);
    // Once the stream has said how it ended, a later error is just the
    // connection closing; never let EventSource reconnect (a new request
    // would start a second deploy).
    let settled = false;

    eventSource.addEventListener("log", (e) => {
      const text = JSON.parse((e as MessageEvent).data) as string;
      setLines((prev) => [...prev, { text }]);
    });

    eventSource.addEventListener("done", () => {
      settled = true;
      setStatus("done");
      eventSource.close();
      void qc.invalidateQueries();
    });

    eventSource.addEventListener("deploy_error", (e) => {
      settled = true;
      const text = JSON.parse((e as MessageEvent).data) as string;
      setLines((prev) => [...prev, { text, error: true }]);
      setStatus("error");
      eventSource.close();
    });

    eventSource.onerror = () => {
      eventSource.close();
      if (!settled) setStatus("lost");
    };

    return () => {
      eventSource.close();
    };
  }, [customerId, qc]);

  // Keep the newest line in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, status]);

  const running = status === "running";
  const showLog = lines.length > 0 || running;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <span className="inline-flex items-center gap-2">
          <StatusIcon status={status} />
          {TITLE[status]}
        </span>
      }
      description={SENTENCE[status]}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {running ? "Close (it keeps running)" : "Close"}
        </Button>
      }
    >
      {showLog && (
        <div
          ref={scrollRef}
          className="max-h-[50vh] overflow-y-auto rounded-control bg-inset p-3 font-mono text-xs leading-relaxed"
        >
          {lines.map((line, i) => (
            <LogEntry key={i} line={line} index={i} />
          ))}
          {running && (
            <div className="mt-1 flex items-center gap-2 text-fg-subtle">
              <Loader2 aria-hidden className="h-3 w-3 animate-spin" strokeWidth={1.75} />
              <span>waiting…</span>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function StatusIcon({ status }: { status: Status }) {
  const cls = "h-4 w-4 shrink-0";
  if (status === "running") return <Loader2 aria-hidden className={cx(cls, "animate-spin text-accent-fg")} strokeWidth={1.75} />;
  if (status === "done") return <CheckCircle aria-hidden className={cx(cls, "text-success")} strokeWidth={1.75} />;
  if (status === "error") return <XCircle aria-hidden className={cx(cls, "text-danger")} strokeWidth={1.75} />;
  return <AlertTriangle aria-hidden className={cx(cls, "text-warning")} strokeWidth={1.75} />;
}

function LogEntry({ line, index }: { line: LogLine; index: number }) {
  // Structlog JSON lines show their event (and container) only.
  let display = line.text;
  let level = "";
  try {
    const parsed = JSON.parse(line.text) as { event?: string; level?: string; container?: string };
    if (parsed.event) {
      level = parsed.level ?? "";
      display = parsed.event;
      if (parsed.container) display += ` [${parsed.container}]`;
    }
  } catch {
    // Not JSON, show as is.
  }

  const isError = line.error || level === "error" || display.toLowerCase().includes("error");
  const isApplyLine = display.startsWith("apply:");

  return (
    <div className={cx("py-0.5 break-words", isError ? "text-danger" : isApplyLine ? "text-accent-fg" : "text-fg-muted")}>
      <span className="mr-2 select-none text-fg-subtle">{String(index + 1).padStart(3, " ")}</span>
      {display}
    </div>
  );
}
